<template>
    <BaseAvatar
        :size="size"
        :data-size="size"
        :disabled="!swatch"
        :style="swatch ? { '--base-avatar-bg': swatch.fill } : undefined"
        class="people-avatar"
        :class="{ 'is-duo': displayInitials.length > 1, 'is-ink': swatch?.peopleInitial === 'ink' }">
        <span v-if="displayInitials">{{ displayInitials }}</span>
        <i v-else class="bi bi-person" aria-hidden="true" />
    </BaseAvatar>
</template>

<script setup lang="ts">
import BaseAvatar, { type BaseAvatarSize } from "~vue/components/BaseAvatar.vue";
import { getAvatarInitials } from "~vue/utils/avatarUtils";
import { getPeopleAvatarSwatch, getPerspectiveSwatch } from "~vue/utils/perspectivePalette";
import { computed } from "vue";

const {
    name,
    initials,
    letters,
    size = "md",
    disabled = false,
} = defineProps<{
    name?: string | null;
    initials?: string;
    /** The person's Perspective type; when known, its hue replaces the colour derived from the name. */
    letters?: string | null;
    size?: BaseAvatarSize;
    disabled?: boolean;
}>();

const displayInitials = computed(() => initials ?? getAvatarInitials(name));
const swatch = computed(() => {
    if (disabled) {
        return null;
    }
    const perspectiveSwatch = getPerspectiveSwatch(letters);
    if (perspectiveSwatch) {
        return perspectiveSwatch;
    }
    return displayInitials.value ? getPeopleAvatarSwatch(name || displayInitials.value) : null;
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .people-avatar {
        @apply font-serif text-[16px] leading-none font-extrabold tracking-normal select-none;
        --base-avatar-text: var(--color-avatar-identity-text);
    }

    .people-avatar[data-size="md"] {
        @apply size-11.25;
    }

    .people-avatar[data-size="sm"] {
        @apply size-10;
    }

    .people-avatar[data-size="xs"] {
        @apply size-8;
    }

    /* Extra Bold is tuned for a single glyph; two letters close up at that weight. */
    .people-avatar.is-duo {
        @apply font-bold;
    }

    .people-avatar.is-ink {
        --base-avatar-text: var(--color-avatar-identity-ink);
    }

    .people-avatar[data-size="xs"],
    .people-avatar[data-size="2xs"] {
        @apply text-[12px] font-bold;
    }

    /* No known type, so no hue: an empty dashed outline instead of a fill. */
    .people-avatar.is-disabled {
        @apply border border-dashed border-avatar-identity-disabled-border bg-transparent text-avatar-identity-disabled-text;
    }
}
</style>
